// vietQrBuilder.ts
// Self-hosted VietQR generator — replaces img.vietqr.io/image dependency.
//
// Builds an EMV TLV payload per the open NAPAS VietQR spec, then renders
// a QR code <canvas> with HonestLee branding (centre logo + "VietQR" badge
// + bank chip in the corner).
//
// Pure client-side. Zero external API calls. Zero cost. Same scan compatibility
// as img.vietqr.io output (any of the 65 Vietnamese banking apps reads it).
//
// Based on the EMVCo MPM spec referenced in CP-0320, validated against the
// EMV parser already in extract-payment-qr-by-media-id.ts.

import QRCode from 'qrcode';

// ─── EMV TLV builder ─────────────────────────────────────────────────────────
const tlv = (id: string, value: string) => id + String(value.length).padStart(2, '0') + value;

// CRC-16/CCITT-FALSE (poly 0x1021, init 0xFFFF) — required by spec
function crc16ccitt(input: string): string {
  let crc = 0xFFFF;
  for (let i = 0; i < input.length; i++) {
    crc ^= input.charCodeAt(i) << 8;
    for (let j = 0; j < 8; j++) {
      crc = (crc & 0x8000) ? ((crc << 1) ^ 0x1021) : (crc << 1);
      crc &= 0xFFFF;
    }
  }
  return crc.toString(16).toUpperCase().padStart(4, '0');
}

export interface VietQrPayloadInput {
  bankBin: string;        // e.g. '970436' for Vietcombank
  accountNumber: string;  // recipient bank account
  amount?: number;        // VND, omit for static QR
  memo?: string;          // up to 25 chars, plain ASCII (no diacritics)
  holderName?: string;    // optional display name (most banks lookup their own)
  serviceCode?: 'QRIBFTTA' | 'QRIBFTTC';  // default QRIBFTTA = Account Transfer
}

/**
 * Build a VietQR EMV TLV string. Output is a plain ASCII string suitable
 * for encoding into any QR code library.
 */
export function buildVietQrPayload({
  bankBin,
  accountNumber,
  amount,
  memo,
  holderName,
  serviceCode = 'QRIBFTTA',
}: VietQrPayloadInput): string {
  // Tag 38: Merchant Account Information (NAPAS-specific nested TLV)
  const beneficiaryNested = tlv('00', bankBin) + tlv('01', accountNumber);
  const merchantNested =
    tlv('00', 'A000000727') +              // GUID = NAPAS
    tlv('01', beneficiaryNested) +
    tlv('02', serviceCode);

  // Build full payload (without CRC tag at end)
  let payload =
    tlv('00', '01') +                                    // Payload format indicator
    tlv('01', amount && amount > 0 ? '12' : '11') +      // 11 = static, 12 = dynamic
    tlv('38', merchantNested) +                          // Merchant info
    tlv('53', '704');                                    // Currency = VND (ISO 4217)

  if (amount && amount > 0) {
    payload += tlv('54', String(Math.round(amount)));
  }

  payload += tlv('58', 'VN');                            // Country code

  if (holderName) {
    // Spec: max 25 chars, ASCII, uppercase
    const cleanedName = holderName.toUpperCase().replace(/[^\x20-\x7E]/g, '').slice(0, 25);
    if (cleanedName) payload += tlv('59', cleanedName);
  }

  if (memo) {
    // Tag 62 (Additional Data) → sub-tag 08 (Purpose of transaction)
    const cleanedMemo = memo.replace(/[^\x20-\x7E]/g, '').slice(0, 25);
    if (cleanedMemo) payload += tlv('62', tlv('08', cleanedMemo));
  }

  // Append CRC tag prefix, compute CRC over the full string including '6304'
  payload += '6304';
  payload += crc16ccitt(payload);

  return payload;
}

// ─── EMV TLV parser (EV-548 — scan a venue's VietQR to pay outside) ──────────
//
// Inverse of buildVietQrPayload. Walks the top-level TLV stream, recurses into
// tag 38 (merchant info → 01 beneficiary → 00 bankBin / 01 account) and tag 62
// (additional data → 08 memo). Tolerant of QRs produced by any banking app, not
// just ours — banks vary in which optional tags they include, so we only require
// tag 38 to resolve a beneficiary.

export interface ParsedVietQr {
  bankBin: string | null;       // 6-digit NAPAS bank BIN
  accountNumber: string | null; // beneficiary account
  amount: number | null;        // VND (null for static QR)
  memo: string | null;          // purpose-of-transaction text, if present
  holderName: string | null;    // merchant/holder name (tag 59), if present
  isVietQr: boolean;            // true only if it parsed as a VietQR-shaped payload
}

/** Walk a flat TLV string into { id → value } pairs (preserves first-seen). */
function walkTlv(input: string): Record<string, string> {
  const out: Record<string, string> = {};
  let i = 0;
  while (i + 4 <= input.length) {
    const id = input.slice(i, i + 2);
    const len = parseInt(input.slice(i + 2, i + 4), 10);
    if (!/^\d{2}$/.test(input.slice(i + 2, i + 4)) || isNaN(len)) break;
    const value = input.slice(i + 4, i + 4 + len);
    if (value.length < len) break;          // truncated / malformed
    if (!(id in out)) out[id] = value;
    i += 4 + len;
  }
  return out;
}

/**
 * Parse a scanned VietQR / EMVCo MPM string back into its beneficiary fields.
 * Returns isVietQr:false if it doesn't look like a VietQR (no usable tag 38).
 */
export function parseVietQrPayload(raw: string): ParsedVietQr {
  const empty: ParsedVietQr = {
    bankBin: null, accountNumber: null, amount: null,
    memo: null, holderName: null, isVietQr: false,
  };
  if (!raw || typeof raw !== 'string') return empty;
  const trimmed = raw.trim();
  // EMVCo payloads start with "0002" (payload format indicator id=00 len=02).
  if (!trimmed.startsWith('00')) return empty;

  const top = walkTlv(trimmed);
  const merchant = top['38'];
  if (!merchant) return empty;

  // Merchant nested: 00=GUID, 01=beneficiary nested, 02=serviceCode
  const merchantNested = walkTlv(merchant);
  const beneficiary = merchantNested['01'];
  let bankBin: string | null = null;
  let accountNumber: string | null = null;
  if (beneficiary) {
    const benNested = walkTlv(beneficiary);
    bankBin = benNested['00'] || null;
    accountNumber = benNested['01'] || null;
  }

  // Amount (tag 54) — VND, optional for static QR
  let amount: number | null = null;
  if (top['54']) {
    const n = parseFloat(top['54']);
    amount = isNaN(n) ? null : n;
  }

  // Memo (tag 62 → sub-tag 08)
  let memo: string | null = null;
  if (top['62']) {
    const additional = walkTlv(top['62']);
    memo = additional['08'] || null;
  }

  const holderName = top['59'] || null;

  return {
    bankBin,
    accountNumber,
    amount,
    memo,
    holderName,
    isVietQr: !!(bankBin && accountNumber),
  };
}

// ─── Branded QR rendering ────────────────────────────────────────────────────

export interface BrandedQrOptions {
  /** size in pixels (final canvas is square) */
  size?: number;
  /** accent color used for the QR squares (default: HonestLee orange) */
  accent?: string;
  /** background color (default: white) */
  bg?: string;
  /** centre logo URL (default: HonestLee mascot — small + white-ringed for scannability) */
  logoUrl?: string | null;
  /** bank code (e.g. 'VCB') — used to fetch + show bank logo top-left */
  bankCode?: string | null;
  /** bank display name (e.g. 'Vietcombank') — shown next to bank chip */
  bankName?: string | null;
}

const HL_ORANGE = '#0F172A';   // Use dark slate for QR squares (better contrast than orange)
const HL_BG = '#FFFFFF';

/**
 * Render a branded VietQR to a canvas element.
 *
 * Layout:
 *   ┌─────────────────────────────┐
 *   │  [VietQR badge]   [Bank ⭕] │   ← top row (compact)
 *   │  ┌───────────────────────┐  │
 *   │  │                       │  │
 *   │  │      QR squares       │  │   ← QR with white-ringed logo in centre
 *   │  │       [HL logo]       │  │
 *   │  │                       │  │
 *   │  └───────────────────────┘  │
 *   │       (account info)        │   ← caption row
 *   └─────────────────────────────┘
 */
export async function renderBrandedQr(
  payload: string,
  canvas: HTMLCanvasElement,
  opts: BrandedQrOptions = {},
): Promise<void> {
  const size = opts.size || 540;
  const accent = opts.accent || HL_ORANGE;
  const bg = opts.bg || HL_BG;
  const HEADER_H = 48;
  const FOOTER_H = 0;
  const QR_PAD = 16;
  const QR_SIZE = size - QR_PAD * 2;

  canvas.width = size;
  canvas.height = size + HEADER_H + FOOTER_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // Fill background
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // ── Header row: VietQR badge (left) + Bank chip (right) ──
  ctx.fillStyle = accent;
  ctx.font = 'bold 20px Inter, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText('VietQR', 16, HEADER_H / 2);

  if (opts.bankName || opts.bankCode) {
    ctx.fillStyle = '#475569';
    ctx.font = '600 14px Inter, system-ui, sans-serif';
    ctx.textAlign = 'right';
    const label = opts.bankName || opts.bankCode || '';
    ctx.fillText(label, size - 16, HEADER_H / 2);
  }

  // Try to draw bank logo (small circle next to bank name on the right) — non-blocking
  if (opts.bankCode) {
    try {
      const logoSize = 28;
      const x = size - 16 - ctx.measureText(opts.bankName || opts.bankCode || '').width - 8 - logoSize;
      const y = (HEADER_H - logoSize) / 2;
      const bankLogo = await loadImage(`https://cdn.vietqr.io/img/${opts.bankCode.toUpperCase()}.png`);
      // White circle background for contrast
      ctx.fillStyle = '#fff';
      ctx.beginPath(); ctx.arc(x + logoSize / 2, y + logoSize / 2, logoSize / 2 + 1, 0, Math.PI * 2); ctx.fill();
      ctx.drawImage(bankLogo, x, y, logoSize, logoSize);
    } catch { /* logo load failed → skip silently */ }
  }

  // ── QR code itself (rendered to a temp canvas first) ──
  const tmp = document.createElement('canvas');
  await QRCode.toCanvas(tmp, payload, {
    width: QR_SIZE,
    margin: 1,
    errorCorrectionLevel: 'H',  // High EC so we can stamp a logo in the centre
    color: { dark: accent, light: bg },
  });
  ctx.drawImage(tmp, QR_PAD, HEADER_H);

  // ── Centre logo (HonestLee) ──
  const logoUrl = opts.logoUrl !== null
    ? (opts.logoUrl || 'https://thai.honestlee.app/icons/wrap128.png')
    : null;
  if (logoUrl) {
    try {
      const logo = await loadImage(logoUrl);
      const logoBox = Math.round(QR_SIZE * 0.20);  // 20% — safe with EC level H
      const cx = QR_PAD + (QR_SIZE - logoBox) / 2;
      const cy = HEADER_H + (QR_SIZE - logoBox) / 2;
      // White rounded background for scannability
      const pad = 6;
      ctx.fillStyle = '#fff';
      roundRect(ctx, cx - pad, cy - pad, logoBox + pad * 2, logoBox + pad * 2, 8);
      ctx.fill();
      ctx.drawImage(logo, cx, cy, logoBox, logoBox);
    } catch { /* logo load failed → skip silently */ }
  }
}

/** Render to a data URL — handy for <img src=...> usage */
export async function renderBrandedQrDataUrl(
  payload: string,
  opts: BrandedQrOptions = {},
): Promise<string> {
  const canvas = document.createElement('canvas');
  await renderBrandedQr(payload, canvas, opts);
  return canvas.toDataURL('image/png');
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.src = url;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

// ─── One-shot helper: build payload + render in one call ─────────────────────
export async function buildAndRenderBrandedQr(
  input: VietQrPayloadInput,
  canvas: HTMLCanvasElement,
  bankName?: string,
): Promise<{ payload: string }> {
  const payload = buildVietQrPayload(input);
  await renderBrandedQr(payload, canvas, {
    bankCode: input.bankBin === '970436' ? 'VCB'
            : input.bankBin === '970422' ? 'MB'
            : input.bankBin === '970416' ? 'ACB'
            : input.bankBin === '970407' ? 'TCB'
            : null,
    bankName: bankName || null,
  });
  return { payload };
}
